import { useState } from "react";
import { ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

export interface ErrorToastProps {
  title: string;
  message: string;
  technicalDetails: string;
  context?: Record<string, unknown>;
  onClose?: () => void;
}

/**
 * Inline toast body content for reportable errors.
 * Errors are auto-reported to the admin and credits are auto-refunded —
 * no manual report button is needed.
 */
export function ErrorToastContent({
  message,
  technicalDetails,
}: ErrorToastProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium leading-snug">{message}</p>
          <p className="text-xs opacity-80 mt-1">
            This issue was automatically reported to our team — we'll fix it and any credits used for this action will be refunded to your account automatically. No action needed on your side.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-1 text-xs underline opacity-90 hover:opacity-100"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Technical details
      </button>

      {expanded && (
        <pre className="text-xs bg-black/20 dark:bg-white/10 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap break-all font-mono">
          {technicalDetails}
        </pre>
      )}
    </div>
  );
}
